<script setup lang="ts">
import StudentSettingsLayout from '@/Layouts/StudentSettingsLayout.vue'
import { CreditCard, Download, AlertCircle, Receipt } from 'lucide-vue-next'

interface Payment {
  id: number
  paid_at: string | null
  amount: string
  currency: string
  status: string
  failure_reason: string | null
  tutor_name: string
  plan_name: string
  stripe_invoice_id: string | null
}

defineProps<{
  payments: Payment[]
}>()

function getStatusClasses(status: string): string {
  switch (status) {
    case 'Succeeded':
      return 'bg-green-100 text-green-800'
    case 'Failed':
      return 'bg-red-100 text-red-800'
    case 'Pending':
      return 'bg-yellow-100 text-yellow-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}
</script>

<template>
  <StudentSettingsLayout>
    <div class="space-y-6">
      <!-- Payment History -->
      <section class="bg-white rounded-2xl shadow-sm p-8">
        <div class="flex items-center gap-3 mb-6">
          <div class="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <CreditCard class="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 class="text-xl font-semibold text-gray-900">Billing & Payments</h2>
            <p class="text-gray-500 text-sm">View your payment history and download invoices</p>
          </div>
        </div>

        <!-- Empty State -->
        <div v-if="payments.length === 0" class="text-center py-12">
          <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Receipt class="w-8 h-8 text-gray-400" />
          </div>
          <h3 class="text-lg font-medium text-gray-900 mb-1">No payments yet</h3>
          <p class="text-gray-500 text-sm">Your payment history will appear here once you subscribe to a tutor.</p>
        </div>

        <!-- Payments Table -->
        <div v-else class="overflow-x-auto">
          <table class="w-full">
            <thead>
              <tr class="border-b border-gray-200">
                <th class="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                <th class="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Tutor</th>
                <th class="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Plan</th>
                <th class="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Amount</th>
                <th class="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th class="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Invoice</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr v-for="payment in payments" :key="payment.id" class="hover:bg-gray-50 transition-colors">
                <td class="py-4 px-4 text-sm text-gray-900">{{ payment.paid_at ?? 'Pending' }}</td>
                <td class="py-4 px-4 text-sm text-gray-900">{{ payment.tutor_name }}</td>
                <td class="py-4 px-4 text-sm text-gray-600">{{ payment.plan_name }}</td>
                <td class="py-4 px-4 text-sm text-gray-900 text-right font-medium">&pound;{{ payment.amount }}</td>
                <td class="py-4 px-4 text-center">
                  <span
                    :class="getStatusClasses(payment.status)"
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                  >
                    {{ payment.status }}
                  </span>
                  <!-- Failure reason tooltip -->
                  <div v-if="payment.failure_reason" class="flex items-center justify-center mt-1 gap-1">
                    <AlertCircle class="w-3 h-3 text-red-400" />
                    <span class="text-xs text-red-500">{{ payment.failure_reason }}</span>
                  </div>
                </td>
                <td class="py-4 px-4 text-right">
                  <a
                    v-if="payment.status === 'Succeeded'"
                    :href="`/student/settings/billing/invoice/${payment.id}`"
                    target="_blank"
                    class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    <Download class="w-3.5 h-3.5" />
                    PDF
                  </a>
                  <span v-else class="text-xs text-gray-400">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  </StudentSettingsLayout>
</template>
